import Vue from 'vue';
import Router from 'vue-router';

import RouterComponent from './components/RouterComponent.vue';
import { saveLastActivity } from '@/utils';
import { isChunkLoadError, reloadForNewBuild } from '@/utils/chunkLoad';
import { ADMINISTRATIVE_FALLBACK_PATH, isBlockedForAdministrative } from '@/utils/administrativeAccess';
import { readUserProfile } from '@/store/main/getters';
import { useDialogStore } from '@/features/dialog/composables/useDialogStore';
import { Store } from 'vuex';

let storeInstance: Store<any> | null = null;

export const setRouterStore = (store: Store<any>) => {
  storeInstance = store;
};

Vue.use(Router);

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    {
      path: '/',
      component: () => import(/* webpackChunkName: "start" */ './views/main/Start.vue'),
      children: [
        {
          path: 'home',
          // route level code-splitting
          // this generates a separate chunk (about.[hash].js) for this route
          // which is lazy-loaded when the route is visited.
          component: () => import(/* webpackChunkName: "home" */ './views/Home.vue'),
        },
        {
          path: 'login',
          component: () => import(/* webpackChunkName: "login" */ './views/Login.vue'),
        },
        {
          path: 'privacy',
          component: () => import(/* webpackChunkName: "privacy" */ './views/Privacy.vue'),
        },
        {
          path: 'recover-password',
          component: () => import(/* webpackChunkName: "recover-password" */ './views/PasswordRecovery.vue'),
        },
        {
          path: 'reset-password',
          component: () => import(/* webpackChunkName: "reset-password" */ './views/ResetPassword.vue'),
        },
        {
          path: 'upload/:secret',
          component: () => import(/* webpackChunkName: "upload-document" */ './views/UploadDocument.vue'),
        },
        {
          path: 'pay-qr',
          component: () => import(/* webpackChunkName: "pay-qr" */ './views/PayQrCode.vue'),
        },
        {
          path: 'main',
          component: () => import(/* webpackChunkName: "main" */ './views/main/Main.vue'),
          children: [
            {
              path: 'profile',
              component: RouterComponent,
              redirect: 'profile/view',
              children: [
                {
                  path: 'view',
                  component: () => import(
                    /* webpackChunkName: "main-profile" */ './views/main/profile/UserProfile.vue'),
                }
              ],
            },
            // praktijkbeheer
            {
              path: 'practice-management',
              component: RouterComponent,
              redirect: 'practice-management/locations',
              children: [
                {
                  path: 'locations',
                  name: 'main-practice-locations',
                  component: () => import(
                    /* webpackChunkName: "main-practice-management" */ './views/main/practice-management/PracticeManagementLocations.vue'),
                }
              ],
            },
            // nomenclaturen
            {
              path: 'nomenclatures',
              component: () => import(/* webpackChunkName: "main-nomenclatures" */ './views/main/Nomenclatures.vue'),
            },
            // patienten
            {
              path: 'patients/create',
              meta: { blockAdministrative: true },
              component: () => import(/* webpackChunkName: "main-nomenclatures" */ './views/main/patients/CreatePatient.vue'),
            },
            {
              path: 'patients/all',
              component: () => import(/* webpackChunkName: "main-nomenclatures" */ './views/main/patients/Patients.vue'),
            },
            {
              path: 'patients/edit/:id',
              meta: { blockAdministrative: true },
              name: 'main-patients-edit',
              component: () => import(
                /* webpackChunkName: "main-admin-users-edit" */ './views/main/patients/EditPatient.vue'),
            },
            {
              path: 'patients/medical-record/:id',
              meta: { blockAdministrative: true },
              name: 'main-patients-medical-record',
              component: () => import(
                /* webpackChunkName: "main-patients-medical-record" */ './views/main/patients/PatientMedicalRecord.vue'),
            },
            {
              path: 'patients/access-logs/:id',
              meta: { blockAdministrative: true },
              name: 'main-patients-access-logs',
              component: () => import(
                /* webpackChunkName: "main-patients-access-logs" */ './views/main/patients/PatientAccessLogs.vue'),
            },
            {
              path: 'patients/zorgrelatie/:id',
              meta: { blockAdministrative: true },
              name: 'main-patients-zorgrelatie',
              component: () => import(
                /* webpackChunkName: "main-patients-zorgrelatie" */ './views/main/patients/PatientZorgrelatie.vue'),
            },
            {
              path: 'patients/journal/:id',
              meta: { blockAdministrative: true },
              name: 'main-patients-journal',
              component: () => import(
                /* webpackChunkName: "main-patients-journal" */ './views/main/patients/PatientJournal.vue'),
            },
            // voorschriften
            {
              path: 'prescriptions/create',
              component: () => import(/* webpackChunkName: "main-nomenclatures" */ './views/main/prescriptions/CreatePrescription.vue'),
            },
            {
              path: 'prescriptions/all',
              meta: { blockAdministrative: true },
              component: () => import(/* webpackChunkName: "main-nomenclatures" */ './views/main/prescriptions/Prescriptions.vue'),
            },
            {
              path: 'prescriptions/edit/:id',
              name: 'main-prescriptions-edit',
              component: () => import(
                /* webpackChunkName: "main-admin-users-edit" */ './views/main/prescriptions/EditPrescription.vue'),
            },
            // getuigschriften
            {
              path: 'attestations/create',
              meta: { blockAdministrative: true },
              component: () => import(/* webpackChunkName: "main-nomenclatures" */ './views/main/attestations/CreateAttestation.vue'),
            },
            {
              path: 'attestations/all',
              meta: { blockAdministrative: true },
              component: () => import(/* webpackChunkName: "main-attestations" */ './views/main/attestations/Attestations.vue'),
            },
            {
              path: 'attestations/detail/:id',
              meta: { blockAdministrative: true },
              name: 'main-attestations-detail',
              component: () => import(
                /* webpackChunkName: "main-admin-users-edit" */ './views/main/attestations/DetailAttestation.vue'),
            },
            // efact
            {
              path: 'efact/create',
              meta: { blockAdministrative: true },
              component: () => import(/* webpackChunkName: "main-efact" */ './views/main/efact/CreateEFact.vue'),
            },
            {
              path: 'efact/all',
              meta: { blockAdministrative: true },
              component: () => import(/* webpackChunkName: "main-efact" */ './views/main/efact/EFact.vue'),
            },
            // invoices
            {
              path: 'invoices/all',
              component: () => import(/* webpackChunkName: "main-invoices" */ './views/main/invoices/Invoices.vue'),
            },
            // financials
            {
              path: 'financials/all',
              component: () => import(/* webpackChunkName: "main-financials" */ './views/main/financials/Financials.vue'),
            },
            // notifications
            {
              path: 'notifications',
              component: () => import(/* webpackChunkName: "main-notifications" */ './views/main/notifications/Notifications.vue'),
            },
            // planning
            {
              path: 'planning/calendar',
              component: () => import(/* webpackChunkName: "main-planning-calendar" */ './views/main/planning/Calendar.vue'),
            },
            {
              path: 'planning/unconfirmed',
              component: () => import(/* webpackChunkName: "main-planning-unconfirmed" */ './views/main/planning/Unconfirmed.vue'),
            },
            {
              path: 'planning/configure',
              name: 'main-planning-configure',
              component: () => import(/* webpackChunkName: "main-planning-configure" */ './views/main/planning/PlannerConfigurationRoot.vue'),
            },
            {
              path: 'planning/configure/create',
              name: 'main-planner-create',
              component: () => import(/* webpackChunkName: "main-planning-configure" */ './views/main/planning/PlannerCreateView.vue'),
            },
            {
              path: 'planning/configure/:id',
              name: 'main-planner-edit',
              component: () => import(/* webpackChunkName: "main-planning-configure" */ './views/main/planning/PlannerEditView.vue'),
            },
            {
              path: 'planning/table',
              component: () => import(/* webpackChunkName: "main-planning-table" */ './views/main/planning/Table.vue'),
            },
            {
              path: 'planning/unsynced',
              component: () => import(/* webpackChunkName: "main-planning-calendar" */ './views/main/planning/Unsynced.vue'),
            },
            {
              path: 'planning/unsynced/edit',
              name: 'main-unsynced-edit',
              component: () => import(/* webpackChunkName: "main-planning-calendar" */ './views/main/planning/EditUnsynced.vue'),
            },
            // documents
            {
              path: 'documents/templates',
              meta: { blockAdministrative: true },
              component: () => import(/* webpackChunkName: "main-documents-templates" */ './views/main/documents/Templates.vue'),
            },
            {
              path: 'documents/third-party-payment',
              meta: { blockAdministrative: true },
              component: () => import(/* webpackChunkName: "main-documents-third-party-payment" */ './views/main/documents/ThirdPartyPayment.vue'),
            },
            // ehbox
            {
              path: 'ehbox',
              meta: { blockAdministrative: true },
              component: () => import(/* webpackChunkName: "main-ehbox" */ './views/main/ehbox/EHBox.vue'),
            },
            // mail templates
            {
              path: 'mail-templates',
              component: () => import(/* webpackChunkName: "main-mail-templates" */ './views/main/MailTemplates.vue'),
            },
            {
              path: 'mail-templates/create',
              component: () => import(/* webpackChunkName: "main-mail-templates-create" */ './views/main/mail-templates/CreateMailTemplate.vue'),
            },
            {
              path: 'mail-templates/edit/:id',
              component: () => import(/* webpackChunkName: "main-mail-templates-edit" */ './views/main/mail-templates/EditMailTemplate.vue'),
            },
            // admin

            {
              path: 'admin',
              component: () => import(/* webpackChunkName: "main-admin" */ './views/main/admin/Admin.vue'),
              redirect: 'admin/users/all',
              children: [
                {
                  path: 'users',
                  redirect: 'users/all',
                },
                {
                  path: 'users/all',
                  component: () => import(
                    /* webpackChunkName: "main-admin-users" */ './views/main/admin/AdminUsers.vue'),
                },
                {
                  path: 'users/edit/:id',
                  name: 'main-admin-users-edit',
                  component: () => import(
                    /* webpackChunkName: "main-admin-users-edit" */ './views/main/admin/EditUser.vue'),
                },
                {
                  path: 'users/create',
                  name: 'main-admin-users-create',
                  component: () => import(
                    /* webpackChunkName: "main-admin-users-create" */ './views/main/admin/CreateUser.vue'),
                },
                {
                  path: 'physiotherapy/all',
                  name: 'main-admin-practices',
                  component: () => import(
                    /* webpackChunkName: "main-admin-practices" */ './views/main/admin/AdminPhysiotherapy.vue'),
                },
                {
                  path: 'physiotherapy/:id',
                  name: 'main-admin-practices-detail',
                  component: () => import(
                    /* webpackChunkName: "main-admin-practices-detail" */ './views/main/admin/AdminPhysiotherapyDetail.vue'),
                },
                {
                  path: 'subscription-plans',
                  name: 'main-admin-subscription-plans',
                  component: () => import(
                    /* webpackChunkName: "main-admin-subscription-plans" */ './views/main/admin/AdminSubscriptionPlans.vue'),
                },
              ],
            },
          ],
        },
      ],
    },
    {
      path: '/maintenance',
      component: () => import(/* webpackChunkName: "maintenance" */ './views/Maintenance.vue'),
    },
    {
      path: '/*', redirect: '/',
    },
  ],
});

// Target of the navigation in progress, for recovering from a failed chunk load
let pendingPath: string | null = null;

router.beforeEach((to, from, next) => {
  pendingPath = to.fullPath;
  const profile = storeInstance ? readUserProfile(storeInstance) : undefined;
  if (profile?.is_administrative && isBlockedForAdministrative(to)) {
    next(ADMINISTRATIVE_FALLBACK_PATH);
    return;
  }
  next();
});

router.onError((error) => {
  if (!isChunkLoadError(error)) return;
  reloadForNewBuild(router.resolve(pendingPath ?? router.currentRoute.fullPath).href);
});

router.afterEach(() => {
  if (storeInstance) {
    const email = readUserProfile(storeInstance)?.email;
    if (email) saveLastActivity(email);
  }
});

router.afterEach(() => {
  if (Vue.prototype.$toast) {
    Vue.prototype.$toast.clear();
  }
});

router.afterEach(() => {
  useDialogStore().closeAllDialogs();
});

export default router;
